import React, { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MagnifyingGlassIcon, ChevronRightIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { KB_CATEGORIES, visibleArticles, categoryLabel, isDev } from '../knowledge/articles'
import { podzielNaTrafienia, rozbijZapytanie, szukaj, wczytajTeksty } from '../knowledge/szukaj'
import { ETYKIETY_TAGOW, GRUPY_TAGOW, etykietyArtykulu, tagiArtykulu } from '../knowledge/tagi'
import BackButton from '../components/BackButton'
import KbCover from '../components/KbCover'
import useDocumentTitle from '../hooks/useDocumentTitle'

const PRZYKLADY = ['Deye', 'Hoymiles', 'PME2', 'LV czy HV', '253 V', 'zgłoszenie do OSD', 'wallbox']

const ILE_TAGOW_NA_START = 10

const liczbaArtykulow = (n) => {
  const d = n % 10
  const s = n % 100
  if (n === 1) return '1 artykuł'
  if (d >= 2 && d <= 4 && !(s >= 12 && s <= 14)) return `${n} artykuły`
  return `${n} artykułów`
}

const data = (iso) => String(iso || '').split('-').reverse().join('.')

const Zaznaczone = ({ tekst, wzorce }) => {
  if (!wzorce.length) return tekst
  return podzielNaTrafienia(tekst, wzorce).map((k, i) =>
    k.trafienie ? (
      <mark key={i} className="bg-blue-100 text-blue-800 rounded-sm">{k.tekst}</mark>
    ) : (
      <React.Fragment key={i}>{k.tekst}</React.Fragment>
    )
  )
}

const KbCard = ({ a, wzorce, fragment }) => (
  <Link
    to={`/baza-wiedzy/${a.slug}`}
    className="group rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow flex flex-col"
  >
    <KbCover cover={a.cover} />
    <div className="p-4 flex flex-col flex-1">
      <h3 className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 leading-snug line-clamp-2">
        <Zaznaczone tekst={a.title} wzorce={wzorce} />
      </h3>
      <p className={`mt-1.5 text-xs text-gray-500 leading-5 ${fragment ? 'line-clamp-3' : 'line-clamp-2'}`}>
        <Zaznaczone tekst={fragment || a.lead} wzorce={wzorce} />
      </p>
      <p className="mt-2 text-[11px] text-blue-600 line-clamp-1">{etykietyArtykulu(a.slug).slice(0, 4).join(' · ')}</p>
      <div className="mt-auto pt-2 flex items-center gap-2 text-[11px] text-gray-400">
        <span>{categoryLabel(a.category)}</span>
        <span>· {a.readingTime} min</span>
        {a.updatedAt && <span>· {data(a.updatedAt)}</span>}
        {isDev && a.status !== 'published' && <span className="ml-auto rounded bg-amber-100 text-amber-700 px-1.5 py-0.5">draft</span>}
      </div>
    </div>
  </Link>
)

const Filtr = ({ aktywny, onClick, etykieta, liczba, maly }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={aktywny}
    disabled={!aktywny && liczba === 0}
    className={`rounded-full border font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      maly ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
    } ${aktywny ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
  >
    {etykieta} <span className={aktywny ? 'text-blue-100' : 'text-gray-400'}>{liczba}</span>
  </button>
)

const KnowledgeBase = () => {
  useDocumentTitle('Baza wiedzy')
  useEffect(() => { window.scrollTo(0, 0) }, [])

  const [params, setParams] = useSearchParams()
  const zapytanie = params.get('q') || ''
  const kategoria = KB_CATEGORIES.some((c) => c.key === params.get('category')) ? params.get('category') : 'all'
  const wybraneTagi = useMemo(
    () => (params.get('tagi') || '').split(',').filter((t) => ETYKIETY_TAGOW[t]),
    [params]
  )
  const [wszystkieTagi, setWszystkieTagi] = useState(false)

  const zmien = (zmiany) => {
    const nowe = new URLSearchParams(params)
    Object.entries(zmiany).forEach(([k, v]) => (v ? nowe.set(k, v) : nowe.delete(k)))
    setParams(nowe, { replace: true })
  }

  const all = useMemo(() => visibleArticles(), [])
  const featured = useMemo(() => all.find((a) => a.featured), [all])

  const [teksty, setTeksty] = useState({})
  const opoznione = useDeferredValue(zapytanie)
  const szuka = opoznione.trim().length > 0

  useEffect(() => {
    if (!szuka) return undefined
    let aktualne = true
    wczytajTeksty(all).then((t) => { if (aktualne) setTeksty(t) })
    return () => { aktualne = false }
  }, [szuka, all])

  const wzorce = useMemo(() => rozbijZapytanie(opoznione), [opoznione])

  // Liczniki filtrów pokazują wyniki bieżącego wyszukiwania.
  const trafienia = useMemo(() => {
    if (!szuka) {
      return [...all]
        .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
        .map((artykul) => ({ artykul, fragment: '' }))
    }
    return szukaj(all, opoznione, teksty)
  }, [all, szuka, opoznione, teksty])

  const wKategorii = useMemo(
    () => (kategoria === 'all' ? trafienia : trafienia.filter((w) => w.artykul.category === kategoria)),
    [trafienia, kategoria]
  )
  const wyniki = useMemo(
    () => wKategorii.filter((w) => wybraneTagi.every((t) => tagiArtykulu(w.artykul.slug).includes(t))),
    [wKategorii, wybraneTagi]
  )
  const licznikiTagow = useMemo(() => {
    const liczniki = {}
    wyniki.forEach((w) => tagiArtykulu(w.artykul.slug).forEach((t) => { liczniki[t] = (liczniki[t] || 0) + 1 }))
    return liczniki
  }, [wyniki])
  const popularneTagi = useMemo(() => {
    const najczestsze = Object.keys(licznikiTagow)
      .filter((t) => !wybraneTagi.includes(t))
      .sort((x, y) => licznikiTagow[y] - licznikiTagow[x] || ETYKIETY_TAGOW[x].localeCompare(ETYKIETY_TAGOW[y], 'pl'))
    return [...wybraneTagi, ...najczestsze.slice(0, ILE_TAGOW_NA_START)]
  }, [licznikiTagow, wybraneTagi])
  const liczbaWKategorii = (key) => trafienia.filter((w) => w.artykul.category === key).length
  const przelaczTag = (tag) => {
    const nowe = wybraneTagi.includes(tag) ? wybraneTagi.filter((t) => t !== tag) : [...wybraneTagi, tag]
    zmien({ tagi: nowe.join(',') })
  }

  const isDefault = !szuka && kategoria === 'all' && !wybraneTagi.length
  const pokazWyroznione = isDefault && featured
  const siatka = pokazWyroznione ? wyniki.filter((w) => w.artykul.slug !== featured.slug) : wyniki
  const opisKategorii = KB_CATEGORIES.find((c) => c.key === kategoria)?.description

  return (
    <div className="px-4 md:px-10 lg:px-24 mt-6 mb-16">
      {/* Breadcrumbs + Powrót */}
      <div className="flex items-center gap-3 flex-wrap">
        <BackButton />
        <nav aria-label="breadcrumb" className="flex items-center gap-1.5 text-sm">
          <Link to="/" className="text-gray-400 hover:text-blue-600 transition-colors">Strona główna</Link>
          <ChevronRightIcon className="w-4 h-4 text-gray-300" />
          <span className="text-gray-700 font-medium">Baza wiedzy</span>
        </nav>
      </div>

      {/* Header */}
      <div className="mt-5 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Baza wiedzy</h1>
        <p className="mt-2 text-gray-500 max-w-2xl leading-7">
          Praktyczne materiały o doborze modułów PV, falowników i magazynów energii. Parametry techniczne,
          porównania i problemy, które mają znaczenie przed zakupem.
        </p>
      </div>

      {/* Wyszukiwanie */}
      <div role="search" className="max-w-2xl">
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={zapytanie}
            onChange={(e) => zmien({ q: e.target.value })}
            placeholder="Szukaj: model, marka, temat..."
            aria-label="Szukaj w bazie wiedzy"
            className="w-full pl-11 pr-11 py-3 rounded-xl border border-gray-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {zapytanie && (
            <button
              type="button"
              onClick={() => zmien({ q: '' })}
              aria-label="Wyczyść wyszukiwanie"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
        {!zapytanie && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
            <span>Na przykład:</span>
            {PRZYKLADY.map((p) => (
              <button key={p} type="button" onClick={() => zmien({ q: p })} className="text-blue-600 hover:underline">{p}</button>
            ))}
          </div>
        )}
      </div>

      {/* Kategorie */}
      <div className="mt-5 flex flex-wrap gap-2">
        <Filtr
          aktywny={kategoria === 'all'}
          onClick={() => zmien({ category: '' })}
          etykieta="Wszystkie"
          liczba={trafienia.length}
        />
        {KB_CATEGORIES.map((c) => (
          <Filtr
            key={c.key}
            aktywny={kategoria === c.key}
            onClick={() => zmien({ category: kategoria === c.key ? '' : c.key })}
            etykieta={c.label}
            liczba={liczbaWKategorii(c.key)}
          />
        ))}
      </div>

      {kategoria !== 'all' && <p className="mt-3 text-sm text-gray-500">{opisKategorii}</p>}

      {/* Tagi */}
      <div className="mt-4">
        {wszystkieTagi ? (
          <div className="space-y-2">
            {GRUPY_TAGOW.map((g) => (
              <div key={g.nazwa} className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm text-gray-500 mr-1">{g.nazwa}:</span>
                {Object.entries(g.tagi).map(([tag, etykieta]) => (
                  <Filtr
                    key={tag}
                    maly
                    aktywny={wybraneTagi.includes(tag)}
                    onClick={() => przelaczTag(tag)}
                    etykieta={etykieta}
                    liczba={licznikiTagow[tag] || 0}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm text-gray-500 mr-1">Tagi:</span>
            {popularneTagi.map((tag) => (
              <Filtr
                key={tag}
                maly
                aktywny={wybraneTagi.includes(tag)}
                onClick={() => przelaczTag(tag)}
                etykieta={ETYKIETY_TAGOW[tag]}
                liczba={licznikiTagow[tag] || 0}
              />
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => setWszystkieTagi(!wszystkieTagi)}
          aria-expanded={wszystkieTagi}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          {wszystkieTagi ? 'Pokaż mniej tagów' : `Wszystkie tagi (${Object.keys(ETYKIETY_TAGOW).length})`}
        </button>
      </div>

      {/* Licznik wyników */}
      <div className="mt-6 mb-4 flex items-center gap-3 flex-wrap" aria-live="polite">
        <h2 className="text-lg font-bold text-gray-900">
          {isDefault ? 'Wszystkie artykuły' : liczbaArtykulow(wyniki.length)}
        </h2>
        {!isDefault && (
          <button
            type="button"
            onClick={() => zmien({ q: '', category: '', tagi: '' })}
            className="text-sm text-blue-600 hover:underline"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {/* Wyróżniony artykuł */}
      {pokazWyroznione && (
        <Link
          to={`/baza-wiedzy/${featured.slug}`}
          className="block rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow mb-6"
        >
          <div className="grid md:grid-cols-[1fr_260px]">
            <div className="p-6 sm:p-7">
              <span className="rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-0.5">{categoryLabel(featured.category)}</span>
              <h2 className="mt-3 text-xl font-bold text-gray-900 leading-snug">{featured.title}</h2>
              <p className="mt-2 text-gray-500 text-sm leading-6">{featured.lead}</p>
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                <ClockIcon className="w-4 h-4" /> {featured.readingTime} min czytania
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">
                Czytaj artykuł <ChevronRightIcon className="w-4 h-4" />
              </span>
            </div>
            <div className="hidden md:flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-blue-50 to-white border-l border-gray-100 p-6">
              <div className="flex items-end gap-1.5" aria-hidden="true">
                <span className="w-3 rounded-t bg-blue-500" style={{ height: 44 }} />
                <span className="w-3 rounded-t bg-blue-300" style={{ height: 26 }} />
                <span className="w-3 rounded-t bg-blue-300" style={{ height: 26 }} />
              </div>
              <span className="text-2xl font-extrabold text-blue-600 leading-none">3,68 kW</span>
              <span className="text-[11px] text-gray-400">1-fazowy czy 3-fazowy</span>
            </div>
          </div>
        </Link>
      )}

      {/* Grid artykułów */}
      {siatka.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {siatka.map((w) => <KbCard key={w.artykul.slug} a={w.artykul} wzorce={wzorce} fragment={w.fragment} />)}
        </div>
      ) : (
        <div className="py-8 text-sm text-gray-500">
          <p>Nie znaleźliśmy artykułu dla tych kryteriów.</p>
          {trafienia.length > 0 && (kategoria !== 'all' || wybraneTagi.length > 0) ? (
            <button
              type="button"
              onClick={() => zmien({ category: '', tagi: '' })}
              className="mt-1 text-blue-600 hover:underline"
            >
              Pokaż wyniki bez filtrów: {liczbaArtykulow(trafienia.length)}
            </button>
          ) : (
            <p className="mt-1">Spróbuj krótszego hasła, samej marki albo modelu.</p>
          )}
        </div>
      )}
    </div>
  )
}

export default KnowledgeBase
